import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLeadForm } from '../../../contexts/LeadFormContext';
import { CATEGORY_LABELS } from '../../../constants/productsData';

const ProductCard = ({ product, onQuickView, onAddToWishlist, onAddToComparison, isInWishlist, isInComparison }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { openLeadForm } = useLeadForm();

  const handleWhatsAppContact = (e) => {
    e?.stopPropagation();
    openLeadForm();
  };

  // Função para obter o nome simplificado do produto no singular
  const getSimpleName = () => {
    if (!product?.categoryId && product?.categoryId !== 0) return product?.name || 'Produto';
    
    const categoryLabel = CATEGORY_LABELS[product.categoryId] || product?.name || 'Produto';
    
    // Converter do plural para o singular
    const singularMap = {
      'Sofás': 'Sofá',
      'Mesas': 'Mesa',
      'Cadeiras': 'Cadeira',
      'Balanços': 'Balanço',
      'Banquetes': 'Banquete',
      'Espreguiçadeiras': 'Espreguiçadeira',
      'Bancos': 'Banco',
      'Poltronas': 'Poltrona'
    };
    
    return singularMap[categoryLabel] || categoryLabel;
  };

  // Função para gerar descrição breve e elegante com linguagem premium
  const getBriefDescription = () => {
    const categoryName = getSimpleName().toLowerCase();
    const descriptions = {
      'mesas': 'Produzida artesanalmente com madeiras nobres e acabamento refinado',
      'mesa': 'Produzida artesanalmente com madeiras nobres e acabamento refinado',
      'sofás': 'Confeccionado à mão com materiais premium e design exclusivo',
      'sofá': 'Confeccionado à mão com materiais premium e design exclusivo',
      'balanços': 'Feito à mão com técnicas artesanais e materiais selecionados',
      'balanço': 'Feito à mão com técnicas artesanais e materiais selecionados',
      'espreguiçadeiras': 'Produzida artesanalmente para momentos de relaxamento',
      'espreguiçadeira': 'Produzida artesanalmente para momentos de relaxamento',
      'poltronas': 'Confeccionada à mão com trabalho artesanal meticuloso',
      'poltrona': 'Confeccionada à mão com trabalho artesanal meticuloso',
      'cadeiras': 'Produzida artesanalmente com materiais nobres',
      'cadeira': 'Produzida artesanalmente com materiais nobres',
      'banquetes': 'Confeccionado artesanalmente com madeiras selecionadas',
      'banquete': 'Confeccionado artesanalmente com madeiras selecionadas',
      'bancos': 'Feito à mão com técnicas artesanais tradicionais',
      'banco': 'Feito à mão com técnicas artesanais tradicionais'
    };

    // Tentar encontrar descrição específica
    for (const [key, value] of Object.entries(descriptions)) {
      if (categoryName.includes(key)) {
        return value;
      }
    }

    // Descrição padrão elegante
    return 'Produzida artesanalmente com materiais selecionados e acabamento refinado';
  };

  // Verificar se é produto de área externa
  const isOutdoor = () => {
    // Balanços, espreguiçadeiras são geralmente de área externa
    const outdoorCategories = [3, 5]; // balancos, espreguicadeiras
    if (outdoorCategories.includes(product?.categoryId)) return true;
    
    // Verificar na descrição se menciona área externa
    const description = product?.description?.toLowerCase() || '';
    return description.includes('área externa') || description.includes('area externa') || 
           description.includes('praia') || description.includes('piscina') || 
           description.includes('jardim') || description.includes('externa');
  };

  return (
    <div 
      className="bg-card rounded-xl border border-border overflow-hidden shadow-sm h-full flex flex-col"
    >
      {/* Image Section */}
      <div 
        className="group relative aspect-[3/2] overflow-hidden bg-muted cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => onQuickView(product)}
      >
        <Image
          src={product?.image}
          alt={product?.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />

        {/* Action Buttons */}
        <div className="absolute top-3 right-3 flex flex-col space-y-2">
          <button
            onClick={(e) => {
              e?.stopPropagation();
              onAddToWishlist(product?.id);
            }}
            className={`w-9 h-9 rounded-full flex items-center justify-center shadow-md transition-all duration-200 ${
              isInWishlist 
                ? 'bg-red-500 text-white' :'bg-white/90 hover:bg-white text-text-primary hover:text-red-500'
            }`}
          >
            <Icon name="Heart" size={16} fill={isInWishlist ? 'currentColor' : 'none'} />
          </button>
          <button
            onClick={(e) => {
              e?.stopPropagation();
              onAddToComparison(product?.id);
            }}
            className={`w-9 h-9 rounded-full flex items-center justify-center shadow-md transition-all duration-200 ${
              isInComparison 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-white/90 hover:bg-white text-text-primary hover:text-primary'
            }`}
          >
            <Icon name="GitCompare" size={16} />
          </button>
        </div>

        {/* Badge Área Externa */}
        {isOutdoor() && (
          <div className="absolute top-3 left-3">
            <span className="px-3 py-1 bg-primary/90 backdrop-blur-sm text-white text-xs font-medium rounded-md">
              Área Externa
            </span>
          </div>
        )}
      </div>

      {/* Content Section - Simplificado */}
      <div className="p-5 flex flex-col flex-grow">
        {/* Tags/Chips */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-5">
          <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full border border-primary/20">
            Artesanal
          </span>
          <span className="px-3 py-1 bg-accent/10 text-accent text-xs font-medium rounded-full border border-accent/20">
            Personalizável
          </span>
          <span className="px-3 py-1 bg-success/10 text-success text-xs font-medium rounded-full border border-success/20">
            Enviamos para todo Brasil
          </span>
        </div>

        {/* Action Button */}
        <div className="mt-auto">
          <Button
            variant="default"
            size="sm"
            iconName="MessageCircle"
            iconPosition="left"
            onClick={handleWhatsAppContact}
            className="w-full bg-gradient-forest flex items-center justify-center gap-2 text-xs py-2.5 font-medium"
          >
            Falar com Consultor
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;